import React, { Fragment, useEffect, useRef, useState } from 'react';
import MilestoneStatusSummary from './MilestoneStatusSummary';
import { ButtonComp } from './VaccinationChart';
import {
    crossIcon,
    leftScrollButton,
    resetIcon,
    rightScrollButton,
    validTickIcon,
} from './common/Icons';
import { IMilestoneCategory, IMilestoneData, UIMilestone } from '../api/getMilestone';
import MilestoneList from './MilestoneList';
import { useDispatch, useSelector } from 'react-redux';
import { getPrescription } from '../store/selectors/prescriptionPad';
import { getPatientByLocalId, putMilestones } from '../store/idbHandler';
import moment from 'moment';
import { getAgeComponents, sideScroll } from '../store/utils';
import { IMilestoneHistory, IMilestoneStatus } from './common/types/milestones';
import MilestoneSelectionModal from './MilestoneSelectionModal';
import { setMilestones, updateMilestones } from '../store/actions/prescriptionPad';
import { MHCWithMilestone } from '../store/types/medicalHistory';
import PadBottomStrip from './prescriptionPad/rightPanel/workspace/padV2/VaccinationBottomStrip';
import addMilestone from '../api/addMilestone';
import { notification } from 'antd';
import { captureError } from '../pages/captureError';
import deleteMilestone from '../api/deleteMilestone';
import AddMilestoneModal from './AddMilestoneModal';
import { generateCategoryMap, getMilestoneSummary } from '../modules/milestone';
import { track } from '../tracking';
import { getMyDbId } from '../api/mactiveid';

const doseBoxWidth = 300;

const buttons = [
    {
        text: 'Achieved',
        iconOutline: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM9.86914 5.93164L6.61914 9.18164C6.36523 9.43555 5.98438 9.43555 5.75586 9.18164L4.13086 7.55664C3.87695 7.32812 3.87695 6.94727 4.13086 6.71875C4.35938 6.46484 4.74023 6.46484 4.99414 6.71875L6.1875 7.91211L9.00586 5.09375C9.23438 4.83984 9.61523 4.83984 9.86914 5.09375C10.0977 5.32227 10.0977 5.70312 9.86914 5.93164Z"
                    fill="#215FFF"
                />
            </svg>
        ),
        status: 'achieved',
    },
    {
        text: 'Under observation',
        iconOutline: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12.2812 7.125C12.2812 5.24609 11.2656 3.51953 9.64062 2.55469C7.99023 1.61523 5.98438 1.61523 4.35938 2.55469C2.70898 3.51953 1.71875 5.24609 1.71875 7.125C1.71875 9.0293 2.70898 10.7559 4.35938 11.7207C5.98438 12.6602 7.99023 12.6602 9.64062 11.7207C11.2656 10.7559 12.2812 9.0293 12.2812 7.125ZM0.5 7.125C0.5 4.81445 1.71875 2.68164 3.75 1.51367C5.75586 0.345703 8.21875 0.345703 10.25 1.51367C12.2559 2.68164 13.5 4.81445 13.5 7.125C13.5 9.46094 12.2559 11.5938 10.25 12.7617C8.21875 13.9297 5.75586 13.9297 3.75 12.7617C1.71875 11.5938 0.5 9.46094 0.5 7.125ZM6.39062 3.67188C6.39062 3.3418 6.64453 3.0625 7 3.0625C7.33008 3.0625 7.60938 3.3418 7.60938 3.67188V6.82031L9.76758 8.24219C10.0469 8.44531 10.123 8.82617 9.94531 9.10547C9.74219 9.38477 9.36133 9.46094 9.08203 9.25781L6.64453 7.63281C6.49219 7.53125 6.39062 7.32812 6.39062 7.125V3.67188Z"
                    fill="#215FFF"
                />
            </svg>
        ),
        status: 'under_observation',
    },
    {
        text: 'Delayed',
        iconOutline: (
            <svg
                width="14"
                height="12"
                viewBox="0 0 14 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M6.79688 1.7832L1.76953 10.0352C1.71875 10.1113 1.71875 10.1621 1.71875 10.2383C1.71875 10.4414 1.87109 10.5938 2.07422 10.5938H11.9004C12.1035 10.5938 12.2812 10.4414 12.2812 10.2383C12.2812 10.1621 12.2559 10.1113 12.2051 10.0352L7.17773 1.7832C7.15234 1.70703 7.07617 1.65625 7 1.65625C6.89844 1.65625 6.84766 1.70703 6.79688 1.7832ZM5.75586 1.14844C6.00977 0.716797 6.49219 0.4375 7 0.4375C7.48242 0.4375 7.96484 0.716797 8.21875 1.14844L13.2461 9.40039C13.3984 9.6543 13.5 9.93359 13.5 10.2383C13.5 11.1016 12.7891 11.8125 11.9004 11.8125H2.07422C1.21094 11.8125 0.5 11.1016 0.5 10.2383C0.5 9.93359 0.576172 9.6543 0.728516 9.40039L5.75586 1.14844ZM7.8125 8.96875C7.8125 9.42578 7.43164 9.78125 7 9.78125C6.54297 9.78125 6.1875 9.42578 6.1875 8.96875C6.1875 8.53711 6.54297 8.15625 7 8.15625C7.43164 8.15625 7.8125 8.53711 7.8125 8.96875ZM7.60938 4.29688V6.73438C7.60938 7.08984 7.33008 7.34375 7 7.34375C6.64453 7.34375 6.39062 7.08984 6.39062 6.73438V4.29688C6.39062 3.9668 6.64453 3.6875 7 3.6875C7.33008 3.6875 7.60938 3.9668 7.60938 4.29688Z"
                    fill="#215FFF"
                />
            </svg>
        ),
        status: 'delayed',
    },
    {
        text: 'Reset',
        iconOutline: resetIcon('w-16 h-16 fill-darwin-accent-bright-blue-800'),
        status: 'reset',
    },
    {
        text: 'Unselect',
        iconOutline: crossIcon('fill-white font-700 w-14 h-12'),
        hidetext: true,
        status: 'unselect',
    },
];

const PatientMilestones: React.FC<{
    milestoneData: UIMilestone;
    updateMilestoneData: (onSuccess?: (() => void) | undefined) => Promise<UIMilestone>;
}> = ({ milestoneData, updateMilestoneData }) => {
    const ageGroups = Object.keys(milestoneData);
    const [selectedMilestones, setSelectedMilestones] = useState<IMilestoneData[]>([]);

    const px = useSelector(getPrescription);

    const [mh, setMh] = useState<MHCWithMilestone>(px?.patientHistory ?? {});

    const [markedMilestones, setMarkedMilestones] = useState<IMilestoneHistory[]>(
        px?.allMilestones?.length
            ? px?.allMilestones
            : (px?.patientHistory as MHCWithMilestone)?.milestones ?? [],
    );
    const [patientDob, setPatientDob] = useState<string>();
    const [modalState, setModalState] = useState<{ visible: boolean; status?: IMilestoneStatus }>({
        visible: false,
    });

    const [currAddMilestoneState, setCurrAddMilestoneState] = useState<{
        ageGroup: {
            age: string;
            maxHrsFromBirth: number;
        };
    }>();

    const dispatch = useDispatch();

    const setSelectedMilestonesCallback = (val: IMilestoneData) => {
        return {
            add: () =>
                setSelectedMilestones((prev) => [
                    ...(prev.filter((p) => p.id !== val.id) ?? []),
                    val,
                ]),
            delete: () => setSelectedMilestones((prev) => prev.filter((p) => p.id !== val.id)),
            toggle: () =>
                setSelectedMilestones((prev) =>
                    prev.find((p) => p.id === val.id)
                        ? prev.filter((p) => p.id !== val.id)
                        : [...(prev.filter((p) => p.id !== val.id) ?? []), val],
                ),
        };
    };

    const setCurrAddMilestoneStateCallback = (value?: {
        ageGroup: {
            age: string;
            maxHrsFromBirth: number;
        };
    }) => {
        setCurrAddMilestoneState(value);
    };

    const setSelectedMilestonesBulkCallback = (values: IMilestoneData[]) => {
        const ids = values.map((v) => v.id);

        return {
            add: () => {
                setSelectedMilestones((prev) => [
                    ...prev.filter((p) => !ids.includes(p.id)),
                    ...values,
                ]);
            },
            delete: () => setSelectedMilestones((prev) => prev.filter((p) => !ids.includes(p.id))),
        };
    };

    const clearSelectedMilestones = () => {
        setSelectedMilestones([]);
    };

    const saveMileStone = (payload: IMilestoneHistory) => {
        track('MILESTONE_SAVE');

        setMarkedMilestones((prev) => {
            const result = [...prev.filter((p) => p.id !== payload.id), payload];
            dispatch(updateMilestones([payload], result));
            return result;
        });
    };

    const saveMileStoneBulk = (payload: IMilestoneHistory[]) => {
        track('MILESTONE_BULK_ACTION');

        const ids = payload.map((r) => r.id);

        setMarkedMilestones((prev) => {
            const result = [...prev.filter((p) => !ids.includes(p.id)), ...payload];
            dispatch(updateMilestones(payload, result));
            return result;
        });
    };

    useEffect(() => {
        if (!Object.keys(milestoneData)?.length) {
            updateMilestoneData();
        }
    }, [milestoneData]);

    const openModalForEdit = (milestoneData: IMilestoneData) => {
        setSelectedMilestones([milestoneData]);
        setModalState({ visible: true, status: undefined });
    };

    const hoursFromBirth = moment().diff(patientDob, 'months') * 730;

    const currAgeGroupIndex = ageGroups.findIndex((ageG) => {
        const milestoneHrs = milestoneData[ageG]?.meta?.hrsFromBirth;
        return hoursFromBirth <= milestoneHrs;
    });

    const isEqual = currAgeGroupIndex
        ? milestoneData[ageGroups[currAgeGroupIndex]]?.meta?.hrsFromBirth === hoursFromBirth
        : false;

    const currAgeGroupIdx =
        currAgeGroupIndex === 0
            ? currAgeGroupIndex
            : isEqual
            ? currAgeGroupIndex
            : currAgeGroupIndex - 1;

    const currAgeGroup = currAgeGroupIdx < 0 ? ageGroups.length - 1 : currAgeGroupIdx;

    useEffect(() => {
        px?.patient.id &&
            getPatientByLocalId(px.patient.id).then((val) => {
                setPatientDob(val.personal.age?.dob);
            });

        const container = ref.current;

        if (container && currAgeGroupIdx) {
            sideScroll(container, (currAgeGroup ? currAgeGroup - 1 : 0) * 300);
        }
    }, [px?.patient, currAgeGroup]);

    useEffect(() => {
        setMh(px?.patientHistory ?? {});
        setMarkedMilestones(
            px?.allMilestones?.length
                ? px?.allMilestones
                : (px?.patientHistory as MHCWithMilestone)?.milestones ?? [],
        );
    }, [px?.patientHistory, px?.allMilestones]);

    const ref = useRef<any>();

    const onLeftClick = () => {
        const container = ref.current;

        if (container) {
            sideScroll(container, -1 * doseBoxWidth);
        }
    };

    const onRightClick = () => {
        const container = ref.current;

        if (container) {
            sideScroll(container, doseBoxWidth);
        }
    };

    const today = moment();
    const ageDuration = moment.duration(today.diff(moment(patientDob)));
    const ageComponents = getAgeComponents(ageDuration);
    const age = ageComponents.ageObj;

    const getAllPrevMilestones = (
        categoryId?: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => {
        const index = ageGroup
            ? ageGroups.findIndex((ageG) => ageG === ageGroup)
            : currAgeGroup + 1;
        const sliceIndex = markprev ? index - 1 : index;

        if (sliceIndex <= 0) {
            return;
        }

        const milestoneArray = ageGroups.slice(0, sliceIndex);

        return milestoneArray.reduce((prev, curr) => {
            const currData = milestoneData[curr].data;
            const milestonesCats = categoryId
                ? currData[categoryId]
                    ? [currData[categoryId]]
                    : []
                : Object.values(currData);

            const milestones = milestonesCats.reduce((previous, current) => {
                const currMilestones = Object.values(current);
                previous = [...previous, ...currMilestones];
                return previous;
            }, [] as IMilestoneData[]);

            prev = [...prev, ...milestones];
            return prev;
        }, [] as IMilestoneData[]);
    };

    const bulkMark = (
        status: IMilestoneStatus,
        categoryId?: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => {
        const previousMilestones = getAllPrevMilestones(categoryId, ageGroup, markprev);
        const isBulkCta = !ageGroup && !categoryId;

        if (previousMilestones) {
            const statusPayload = previousMilestones.map((p) => ({
                id: p.id,
                age,
                status: isBulkCta
                    ? p.ageGroup === ageGroups[currAgeGroup]
                        ? 'achieved'
                        : status
                    : status,
                categoryId: categoryId ?? p.categoryId,
                name: p.name,
                category: p.category,
            }));

            saveMileStoneBulk(statusPayload);
        }
    };

    const clearMilestonesCallback = (
        clearedMilestones?: IMilestoneHistory[],
        isReset?: boolean,
    ) => {
        const allMilestones = px?.allMilestones?.length ? px?.allMilestones : mh?.milestones ?? [];
        const finalClearedMilestone =
            (clearedMilestones ?? px?.milestones)?.map((m) => ({
                ...m,
                age: undefined,
                notes: undefined,
                status: undefined,
            })) ?? [];

        const milestoneIdList = finalClearedMilestone?.map((c) => c.id);

        const finalAllMilestones = [
            ...allMilestones.filter((m) => !milestoneIdList?.includes(m.id)),
            ...(isReset ? [] : mh.milestones?.filter((m) => milestoneIdList?.includes(m.id)) ?? []),
            ...finalClearedMilestone,
        ];

        const finalMilestones = [
            ...(px?.milestones?.filter((m) => !milestoneIdList?.includes(m.id)) ?? []),
            ...finalClearedMilestone,
        ];

        dispatch(setMilestones(finalAllMilestones, finalMilestones));

        setMarkedMilestones(finalAllMilestones);

        clearedMilestones && setSelectedMilestones([]);
    };

    const markPreviousMilestonesAsAchieved = (
        status: IMilestoneStatus,
        categoryId: IMilestoneCategory,
        ageGroup?: string,
    ) => {
        bulkMark(status, categoryId, ageGroup);
        notification.success({ message: 'Milestones marked successfully', duration: 3 });
    };

    const closeAddMilestoneModal = () => setCurrAddMilestoneState(undefined);

    const addMilestoneCallback = async (milestone: IMilestoneData) => {
        await putMilestones(getMyDbId() || '', {
            ...milestone,
            created_at: new Date(),
            updated_at: new Date(),
        });

        addMilestone(milestone)
            .then((res) => {
                if (res.error) {
                    throw new Error(res.error);
                }

                notification.success({
                    message: 'Successfully added milestone',
                    duration: 3,
                });

                updateMilestoneData(() => {
                    const el = document.getElementById(`add-milestone-${milestone.ageGroup}`);

                    if (el) {
                        el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end',
                            inline: 'nearest',
                        });
                    }
                });
                closeAddMilestoneModal();
            })
            .catch((e) => {
                notification.error({
                    message: 'Error occured while adding milestone',
                    duration: 3,
                });
                captureError(e);
            });
    };

    const deleteMilestoneCallback = async (milestone: IMilestoneData) => {
        const deleteMilestoneObj: IMilestoneData = {
            ...milestone,
            archived: true,
            updated_at: new Date(),
        };
        await putMilestones(getMyDbId() || '', deleteMilestoneObj);

        deleteMilestone(deleteMilestoneObj)
            .then((res) => {
                if (res.error) {
                    throw new Error(res.error);
                }

                notification.success({
                    message: 'Successfully deleted milestone',
                    duration: 3,
                });

                updateMilestoneData();
            })
            .catch((e) => {
                notification.error({
                    message: 'Error occured while deleting milestone',
                    duration: 3,
                });
                captureError(e);
            });
    };

    const milestonesTillCurrAge = ageGroups
        .slice(0, currAgeGroup === 0 ? 1 : currAgeGroup + 1)
        .reduce((prev, curr) => {
            const data = milestoneData[curr].data;
            const milestones = Object.values(data);

            return [
                ...prev,
                ...milestones.reduce((prev1, curr1) => {
                    const m = Object.values(curr1);
                    return [...prev1, ...Object.values(m)];
                }, [] as IMilestoneData[]),
            ];
        }, [] as IMilestoneData[]);

    const milestoneSummary: {
        result?: Record<
            IMilestoneCategory,
            { achieved?: boolean; under_observation?: boolean; delayed?: boolean }
        >;
        statuses?: IMilestoneHistory[];
    } = getMilestoneSummary(milestonesTillCurrAge, markedMilestones);

    const ageGroupTillCurrage = ageGroups.slice(0, currAgeGroup);
    const categoryMap = generateCategoryMap(ageGroupTillCurrage, milestoneData, markedMilestones);

    return (
        <Fragment>
            <div className="flex flex-col gap-12 bg-darwin-neutral_new-50 h-full w-full">
                <div
                    className={`flex justify-between border-b border-darwin-neutral_new-200 items-center w-full px-12 py-8 ${
                        selectedMilestones.length ? 'bg-darwin-accent-bright-blue-800' : ''
                    }`}
                >
                    {selectedMilestones.length ? (
                        <Fragment>
                            <p className="text-14 font-700 text-white">
                                {selectedMilestones.length}{' '}
                                {selectedMilestones.length <= 1 ? 'milestone' : 'milestones'}{' '}
                                selected
                            </p>
                            <div className="flex items-center gap-8">
                                <p className="text-13 font-400 text-white">Update:</p>
                                {buttons.map((btn) => {
                                    return btn.status === 'unselect' ? (
                                        <span
                                            className="cursor-pointer"
                                            onClick={() => setSelectedMilestones([])}
                                        >
                                            {btn.iconOutline}
                                        </span>
                                    ) : (
                                        <ButtonComp
                                            onClick={() => {
                                                if (btn.status === 'reset') {
                                                    clearMilestonesCallback(
                                                        selectedMilestones.map((m) => ({
                                                            id: m.id,
                                                            age: undefined,
                                                            notes: undefined,
                                                            status: undefined,
                                                            categoryId: m.categoryId,
                                                            name: m.name,
                                                            category: m.category,
                                                        })),
                                                        true,
                                                    );
                                                } else {
                                                    setModalState({
                                                        visible: true,
                                                        status: btn.status as IMilestoneStatus,
                                                    });
                                                }
                                            }}
                                        >
                                            <span>{btn.iconOutline}</span>
                                            <p>{btn.text}</p>
                                        </ButtonComp>
                                    );
                                })}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <MilestoneStatusSummary milestoneSummary={milestoneSummary} />
                            <ButtonComp
                                onClick={() => {
                                    bulkMark('achieved_unknown');
                                    notification.success({
                                        message: 'Milestones marked successfully',
                                        duration: 3,
                                    });
                                }}
                            >
                                <span className="rounded-full p-3 bg-darwin-accent-bright-blue-800 flex justify-center items-center">
                                    {validTickIcon('w-8 h-8 fill-white stroke-1 font-700', 'white')}
                                </span>
                                <p>Mark milestones achieved as per age</p>
                            </ButtonComp>
                        </Fragment>
                    )}
                </div>
                <div ref={ref} className="flex px-12 mb-80 overflow-auto hide-scrollbar gap-16">
                    {ageGroups.map((ageG, idx) => (
                        <MilestoneList
                            selectedMilestones={selectedMilestones}
                            setSelectedMilestones={setSelectedMilestonesCallback}
                            setSelectedMilestonesBulk={setSelectedMilestonesBulkCallback}
                            data={milestoneData[ageG]}
                            ageGroup={ageG}
                            currAgeGroupIdx={currAgeGroup}
                            index={idx}
                            age={age}
                            markedMilestones={markedMilestones}
                            saveMileStoneBulk={saveMileStoneBulk}
                            saveMileStone={saveMileStone}
                            openModalForEdit={openModalForEdit}
                            markPreviousMilestonesAsAchieved={markPreviousMilestonesAsAchieved}
                            categoryMap={categoryMap}
                            setCurrAddMilestoneStateCallback={setCurrAddMilestoneStateCallback}
                            hrsFromBirth={milestoneData[ageG]?.meta?.hrsFromBirth}
                            deleteMilestoneCallback={deleteMilestoneCallback}
                            clearMilestonesCallback={clearMilestonesCallback}
                            getAllPrevMilestones={getAllPrevMilestones}
                            getMilestoneSummary={getMilestoneSummary}
                        />
                    ))}
                </div>
                <PadBottomStrip clearSelections={clearMilestonesCallback} />
            </div>
            <div
                onClick={onLeftClick}
                style={{ top: '50%' }}
                className="flex z-10 absolute left-16 top-0 cursor-pointer justify-center items-center w-28 h-28 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-4"
            >
                {leftScrollButton()}
            </div>
            <div
                onClick={onRightClick}
                style={{ top: '50%' }}
                className="absolute z-10 right-16 top-0 flex cursor-pointer justify-center items-center w-28 h-28 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-4"
            >
                {rightScrollButton()}
            </div>
            <MilestoneSelectionModal
                isVisible={!!selectedMilestones.length && modalState.visible}
                closeModal={(prevStatus?: string) => {
                    if (!prevStatus) {
                        clearSelectedMilestones();
                    }
                    setModalState({ visible: false, status: undefined });
                }}
                selectedMilestones={selectedMilestones}
                initialStatus={selectedMilestones.map((m) => {
                    const currstatus = markedMilestones.find((val) => val.id === m.id);

                    return {
                        id: m.id,
                        status: (modalState.status ?? currstatus?.status) as IMilestoneStatus,
                        age:
                            currstatus?.status === 'delayed' ||
                            currstatus?.status === 'under_observation' ||
                            modalState.status === 'delayed' ||
                            modalState.status === 'under_observation'
                                ? undefined
                                : currstatus?.age ?? ageComponents.ageObj,
                        notes: currstatus?.notes,
                        categoryId: currstatus?.categoryId ?? m.categoryId,
                        name: currstatus?.name ?? m.name,
                        category: currstatus?.category ?? m.category,
                    };
                })}
                saveMileStoneBulk={saveMileStoneBulk}
                clearSelectedMilestones={clearSelectedMilestones}
                modalStatus={modalState.status}
                markPreviousMilestonesAsAchieved={bulkMark}
                markedMilestones={markedMilestones}
                getAllPrevMilestones={getAllPrevMilestones}
                getMilestoneSummary={getMilestoneSummary}
            />
            <AddMilestoneModal
                closeModal={() => setCurrAddMilestoneStateCallback(undefined)}
                addMilestoneCallback={addMilestoneCallback}
                currAddMilestoneState={currAddMilestoneState}
            />
        </Fragment>
    );
};

export default PatientMilestones;
